import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ArrowRight, Phone, Award } from 'lucide-react';
import { Button } from './ui/button';
import { heroContent, businessInfo } from '../data/mock';

const HeroSection = () => {
  const heroRef = useRef(null);
  const glowLineRef = useRef(null);
  const textRef = useRef(null);
  const badgeRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Initial animations
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

      // Glow line animation
      tl.fromTo(
        glowLineRef.current,
        { scaleX: 0, opacity: 0 },
        { scaleX: 1, opacity: 1, duration: 1.2 }
      );

      // Text animations
      tl.fromTo(
        '.hero-headline',
        { y: 60, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8 },
        '-=0.6'
      );

      tl.fromTo(
        '.hero-subheadline',
        { y: 40, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8 },
        '-=0.5'
      );

      tl.fromTo(
        '.hero-description',
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.7 },
        '-=0.4'
      );

      tl.fromTo(
        '.hero-cta',
        { y: 20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.6, stagger: 0.15 },
        '-=0.3'
      );

      tl.fromTo(
        badgeRef.current,
        { scale: 0.8, opacity: 0 },
        { scale: 1, opacity: 1, duration: 0.6 },
        '-=0.4'
      );
    }, heroRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      {/* Fleet background image */}
      <div className="absolute inset-0">
        <img
          src="/images/securelec-fleet-hero.webp"
          alt=""
          aria-hidden="true"
          loading="eager"
          fetchPriority="high"
          className="h-full w-full object-cover object-[62%_center] sm:object-[58%_center] lg:object-center"
        />
        {/* Strongest behind the left-aligned copy, opening up over the fleet */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/55 to-black/25 md:from-black/75 md:via-black/45 md:to-black/5" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-black/35" />
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 pt-32 pb-16 relative z-10">
        <div className="max-w-3xl">
          {/* Experience Badge */}
          <div
            ref={badgeRef}
            className="inline-flex items-center gap-2 bg-securelec-red/20 backdrop-blur-sm border border-securelec-red/40 rounded-full px-4 py-2 mb-8"
          >
            <Award className="w-4 h-4 text-securelec-red" />
            <span className="text-securelec-red text-sm font-semibold tracking-wide">
              {heroContent.badge}
            </span>
          </div>

          {/* Glow Line Accent */}
          <div
            ref={glowLineRef}
            className="w-32 h-1 bg-gradient-to-r from-securelec-red to-securelec-red/50 mb-8 origin-left rounded-full"
            style={{ boxShadow: '0 0 20px rgba(229, 33, 51, 0.6)' }}
          />

          {/* Main Headline */}
          <h1 ref={textRef} className="mb-6">
            <span className="hero-headline block text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-3 tracking-tight drop-shadow-lg">
              {heroContent.headline}
            </span>
            <span className="hero-subheadline block text-3xl md:text-4xl lg:text-5xl font-bold text-securelec-red drop-shadow-lg">
              {heroContent.subheadline}
            </span>
          </h1>

          {/* Description */}
          <p className="hero-description text-lg md:text-xl text-white/80 mb-10 leading-relaxed max-w-2xl drop-shadow-md">
            {heroContent.description}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-start gap-4">
            <a href="/get-a-quote">
              <Button
                className="hero-cta bg-securelec-red hover:bg-securelec-red/90 text-white px-8 py-6 text-lg rounded-md font-semibold relative overflow-hidden group transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-securelec-red/30"
                data-testid="hero-get-a-quote-btn"
              >
                <span className="relative z-10 flex items-center gap-2">
                  {heroContent.ctaPrimary}
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </span>
              </Button>
            </a>
          </div>

          {/* Trust indicators */}
          <div className="hero-cta flex flex-wrap items-center gap-6 mt-10 pt-8 border-t border-white/20">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              <span className="text-white/70 text-sm">Available 7 Days</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-securelec-red rounded-full" />
              <span className="text-white/70 text-sm">Licensed & Insured</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-securelec-red rounded-full" />
              <span className="text-white/70 text-sm">Melbourne & VIC</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Gradient Fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent" />

      {/* Decorative Corner Elements */}
      <div className="absolute top-24 right-10 w-20 h-20 border-r-2 border-t-2 border-securelec-red/30 hidden lg:block" />
      <div className="absolute bottom-32 right-10 w-32 h-32 border-r-2 border-b-2 border-white/10 hidden lg:block" />
    </section>
  );
};

export default HeroSection;
